import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { serviceProviders } from '../data/serviceProviders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceRatingDialog = ({ open, onClose, providerName: initialProviderName, providerCategory: initialCategory }) => {
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Get all providers flattened
  const allProviders = Object.entries(serviceProviders).flatMap(([category, providers]) =>
    providers.map(p => ({ ...p, category }))
  );

  const categoryMap = {
    'staging': 'Home Staging',
    'photography': 'Photography & Videography',
    'painting': 'Painting Services',
    'cleaning': 'Cleaning Services',
    'handyman': 'Handyman Services',
    'landscaping': 'Landscaping & Yard Work',
    'titleCompanies': 'Title Companies',
    'attorneys': 'Real Estate Attorneys',
    'homeInspectors': 'Home Inspectors'
  };

  const handleSubmit = async () => {
    const providerToRate = initialProviderName || selectedProvider;
    const categoryToUse = initialCategory || selectedCategory;

    if (!providerToRate) {
      toast.error('Please select a service provider');
      return;
    }
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (!userEmail || !userEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/service-ratings/submit`, {
        provider_name: providerToRate,
        provider_category: categoryToUse,
        rating: rating,
        review: review || null,
        user_name: userName || null,
        user_email: userEmail,
        service_used: true
      });

      toast.success('Thank you for your feedback!');
      onClose();
      
      // Reset form
      setSelectedProvider('');
      setSelectedCategory('');
      setRating(0);
      setReview('');
      setUserName('');
      setUserEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex gap-2 justify-center my-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="transition-transform hover:scale-110"
          >
            <Star
              className={`w-10 h-10 ${
                star <= (hoverRating || rating)
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {initialProviderName ? `Rate Your Experience with ${initialProviderName}` : 'Rate a Service Provider'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!initialProviderName && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Which service did you use? *
              </label>
              <Select value={selectedProvider} onValueChange={(value) => {
                setSelectedProvider(value);
                const provider = allProviders.find(p => p.name === value);
                if (provider) {
                  setSelectedCategory(categoryMap[provider.category] || provider.category);
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a service provider" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {allProviders.map((provider, idx) => (
                    <SelectItem key={idx} value={provider.name}>
                      {provider.name} ({categoryMap[provider.category] || provider.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-600 text-center mb-2">
              How would you rate your experience?
            </p>
            {renderStars()}
            {rating > 0 && (
              <p className="text-center text-sm font-medium text-gray-700">
                {rating === 1 && '😞 Poor'}
                {rating === 2 && '😕 Fair'}
                {rating === 3 && '😐 Good'}
                {rating === 4 && '😊 Very Good'}
                {rating === 5 && '🤩 Excellent'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Name (Optional)
            </label>
            <Input
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Email *
            </label>
            <Input
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="john@example.com"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              We'll never share your email or spam you
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review (Optional)
            </label>
            <Textarea
              value={review}
              onChange={(e) => setReview(e.target.value)}
              placeholder="Tell us about your experience with this service provider..."
              rows={4}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-900">
              💡 Your feedback helps other FSBO sellers find quality service providers!
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || rating === 0}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {loading ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceRatingDialog;
